import { type HTMLAttributes, forwardRef, type ReactNode } from 'react'
import { cn } from '@lib/utils'

export interface TagProps extends HTMLAttributes<HTMLSpanElement> {
  /** Tag variant/priority */
  variant?: 'default' | 'target' | 'growth' | 'monitor' | 'opportunity'
  /** Size */
  size?: 'sm' | 'md' | 'lg'
  /** Icon to show */
  icon?: ReactNode
  /** Show remove button */
  removable?: boolean
  /** Remove handler */
  onRemove?: () => void
}

/**
 * Tag component for keyword priorities and labels
 *
 * @example
 * <Tag variant="target" icon="🎯">plumber near me</Tag>
 * <Tag variant="growth" removable onRemove={handleRemove}>emergency plumber</Tag>
 */
export const Tag = forwardRef<HTMLSpanElement, TagProps>(
  (
    {
      className,
      variant = 'default',
      size = 'md',
      icon,
      removable = false,
      onRemove,
      children,
      ...props
    },
    ref
  ) => {
    const getVariantStyles = () => {
      switch (variant) {
        case 'target':
          return 'bg-priority-target/20 text-priority-target border-priority-target'
        case 'growth':
          return 'bg-priority-growth/20 text-priority-growth border-priority-growth'
        case 'monitor':
          return 'bg-priority-monitor/20 text-priority-monitor border-priority-monitor'
        case 'opportunity':
          return 'bg-priority-opportunity/20 text-priority-opportunity border-priority-opportunity'
        default:
          return 'bg-surface-elevated text-text-primary border-border'
      }
    }

    const getDefaultIcon = () => {
      switch (variant) {
        case 'target':
          return '🎯'
        case 'growth':
          return '📈'
        case 'monitor':
          return '👁'
        case 'opportunity':
          return '💡'
        default:
          return null
      }
    }

    const displayIcon = icon ?? getDefaultIcon()

    return (
      <span
        ref={ref}
        className={cn(
          // Base styles
          'inline-flex items-center gap-1.5 border font-medium',

          // Size variants
          size === 'sm' && 'px-2 py-0.5 text-caption',
          size === 'md' && 'px-2.5 py-1 text-body-sm',
          size === 'lg' && 'px-3 py-1.5 text-body',

          // Variant styles
          getVariantStyles(),

          className
        )}
        {...props}
      >
        {displayIcon && <span className="shrink-0">{displayIcon}</span>}
        <span>{children}</span>
        {removable && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onRemove?.()
            }}
            className="shrink-0 ml-1 hover:opacity-70 transition-opacity"
            aria-label="Remove tag"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </span>
    )
  }
)

Tag.displayName = 'Tag'

/**
 * Keyword tag type badge
 */
export interface KeywordTypeBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Keyword type */
  type: 'city' | 'near' | 'research' | 'comparison' | 'emergency' | 'service'
  /** Size */
  size?: 'sm' | 'md'
}

export const KeywordTypeBadge = forwardRef<HTMLSpanElement, KeywordTypeBadgeProps>(
  ({ className, type, size = 'sm', ...props }, ref) => {
    const typeConfig: Record<string, { label: string; color: string }> = {
      city: { label: 'City', color: 'bg-info/20 text-info' },
      near: { label: 'Near', color: 'bg-success/20 text-success' },
      research: { label: 'Research', color: 'bg-primary-muted text-primary' },
      comparison: { label: 'Compare', color: 'bg-warning/20 text-warning' },
      emergency: { label: 'Emergency', color: 'bg-error/20 text-error' },
      service: { label: 'Service', color: 'bg-surface-elevated text-text-secondary' },
    }

    const config = typeConfig[type]

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center font-medium',
          size === 'sm' && 'px-1.5 py-0.5 text-caption',
          size === 'md' && 'px-2 py-1 text-body-sm',
          config.color,
          className
        )}
        {...props}
      >
        {config.label}
      </span>
    )
  }
)

KeywordTypeBadge.displayName = 'KeywordTypeBadge'

/**
 * Tag group for multiple tags
 */
export interface TagGroupProps {
  /** Tags to display */
  tags: { id: string; label: string; variant?: TagProps['variant'] }[]
  /** Max tags to show before "+N more" */
  maxVisible?: number
  /** Remove handler */
  onRemove?: (id: string) => void
  /** Click handler */
  onClick?: (id: string) => void
  /** Size */
  size?: 'sm' | 'md' | 'lg'
}

export function TagGroup({
  tags,
  maxVisible = 5,
  onRemove,
  onClick,
  size = 'md',
}: TagGroupProps) {
  const visibleTags = tags.slice(0, maxVisible)
  const hiddenCount = tags.length - maxVisible

  return (
    <div className="flex flex-wrap gap-2">
      {visibleTags.map((tag) => (
        <Tag
          key={tag.id}
          variant={tag.variant}
          size={size}
          removable={!!onRemove}
          onRemove={() => onRemove?.(tag.id)}
          onClick={() => onClick?.(tag.id)}
          className={onClick ? 'cursor-pointer' : undefined}
        >
          {tag.label}
        </Tag>
      ))}
      {hiddenCount > 0 && (
        <span
          className={cn(
            'inline-flex items-center text-text-muted',
            size === 'sm' && 'text-caption',
            size === 'md' && 'text-body-sm',
            size === 'lg' && 'text-body'
          )}
        >
          +{hiddenCount} more
        </span>
      )}
    </div>
  )
}

export default Tag
